import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, ButtonGroup, Button } from 'react-bootstrap';
import {
    DollarSign,
    Calendar,
    ShoppingCart,
    BarChart3
} from 'lucide-react';
import {
    LineChart, Line, Tooltip, BarChart, Bar, XAxis, YAxis, ResponsiveContainer
} from 'recharts';
import { OrderGateway } from '../../gateways/OrderGateway';
import { Utils } from '../../common/Utils';
import { useGlobal } from '../../contexts/GlobalContext';

const EarningsAnalytics: React.FC = () => {
    const [revenueFilter, setRevenueFilter] = useState<'hourly' | 'daily' | 'monthly'>('monthly');
    const [revenueTrendToggle, setRevenueTrendToggle] = useState<'actual' | 'trend'>('actual');
    const [orderFilter, setOrderFilter] = useState<'hourly' | 'daily' | 'monthly'>('monthly');
    const [orderTrendToggle, setOrderTrendToggle] = useState<'actual' | 'trend'>('actual');
    const [effectiveDate] = useState(new Date());

    const generateEmptyOrderData = () => {
        // Hourly: 24 hours
        const hourly = Array.from({ length: 24 }, (_, i) => {
            const hour = i % 12 === 0 ? 12 : i % 12;
            const period = i < 12 ? 'AM' : 'PM';
            return { name: `${hour} ${period}`, orders: 0, revenue: 0 };
        });

        // Daily: Last 7 days
        const daily = Array.from({ length: 7 }, (_, i) => {
            const date = new Date(effectiveDate.toDateString());
            date.setDate(effectiveDate.getDate() - (6 - i));
            return { name: date.toLocaleDateString('en-US', { weekday: 'short' }), orders: 0, revenue: 0 };
        });

        // Monthly: Jan to Dec
        const monthly = Array.from({ length: 12 }, (_, i) => {
            const date = new Date(effectiveDate.toDateString());
            date.setMonth(effectiveDate.getMonth() - (11 - i));
            return { name: date.toLocaleDateString('en-US', { month: 'short' }), orders: 0, revenue: 0 };
        });

        return { hourly, daily, monthly };
    };

    const [orderData, setOrderData] = useState(generateEmptyOrderData());
    const [orderTrendData, setOrderTrendData] = useState(generateEmptyOrderData());
    const [revenueData, setRevenueData] = useState(generateEmptyOrderData());
    const [revenueTrendData, setRevenueTrendData] = useState(generateEmptyOrderData());

    type BestSellingProduct = { name: string; orders: number; revenue: number; change: number };
    const [stats, setStats] = useState({
        dailyRevenue: 0,
        monthlyRevenue: 0,
        dailyOrders: 0,
        monthlyOrders: 0,
        dailyRevenueChange: 0,
        monthlyRevenueChange: 0,
        dailyOrdersChange: 0,
        monthlyOrdersChange: 0
    });

    const [bestSellingProducts, setBestSellingProducts] = useState<BestSellingProduct[]>([]);    
    const { settings } = useGlobal();
    const colors = ['#111827','#577CC6'];


    useEffect(() => {
        const fetchOrders = async () => {
            const orders = await OrderGateway.GetOrdersForAnalytics(effectiveDate);
            const productStats = new Map<string, { orders: number, revenue: number, prevMonthOrders: number }>();

            // Customer distribution calculation
            const customerOrderCounts = new Map<string, number>();
            const allCustomerOrderCounts = new Map<string, number>();
            const prevMonthCustomerOrderCounts = new Map<string, number>();
            const currentMonthFilterDate = new Date(effectiveDate.toDateString());
            currentMonthFilterDate.setDate(1);
            const previousMonthFilterDate = new Date(effectiveDate.toDateString());
            previousMonthFilterDate.setMonth(effectiveDate.getMonth() - 1);
            previousMonthFilterDate.setDate(1);

            // ...existing widget stats...
            const yesterday = new Date(effectiveDate);
            yesterday.setDate(effectiveDate.getDate() - 1);
            const lastMonth = new Date(effectiveDate);
            lastMonth.setMonth(effectiveDate.getMonth() - 1);
            let dailyRevenue = 0, monthlyRevenue = 0, dailyOrders = 0, monthlyOrders = 0;
            let prevDailyRevenue = 0, prevMonthlyRevenue = 0, prevDailyOrders = 0, prevMonthlyOrders = 0;

            const fullHourly = generateEmptyOrderData().hourly;
            const fullDaily = generateEmptyOrderData().daily;
            const fullMonthly = generateEmptyOrderData().monthly;

            const hourlyMap = new Map(fullHourly.map(item => [item.name, 0]));
            const dailyMap = new Map(fullDaily.map(item => [item.name, 0]));
            const monthlyMap = new Map(fullMonthly.map(item => [item.name, 0]));
            const hourlyTrendMap = new Map(fullHourly.map(item => [item.name, 0]));
            const dailyTrendMap = new Map(fullDaily.map(item => [item.name, 0]));
            const monthlyTrendMap = new Map(fullMonthly.map(item => [item.name, 0]));

            const hourlyRevenueMap = new Map(fullHourly.map(item => [item.name, 0]));
            const dailyRevenueMap = new Map(fullDaily.map(item => [item.name, 0]));
            const monthlyRevenueMap = new Map(fullMonthly.map(item => [item.name, 0]));
            const hourlyRevenueTrendMap = new Map(fullHourly.map(item => [item.name, 0]));
            const dailyRevenueTrendMap = new Map(fullDaily.map(item => [item.name, 0]));
            const monthlyRevenueTrendMap = new Map(fullMonthly.map(item => [item.name, 0]));

            const hourlyFilterDate = new Date(effectiveDate.toDateString());
            const dailyFilterDate = new Date(effectiveDate.toDateString());
            dailyFilterDate.setDate(effectiveDate.getDate() - 6);
            const monthlyFilterDate = new Date(effectiveDate.toDateString());
            monthlyFilterDate.setMonth(effectiveDate.getMonth() - 11);
            monthlyFilterDate.setDate(1);

            orders.forEach(({ order }) => {
                const createdAt = new Date(order.createdAt);
                const total = parseFloat(order.totalAmount);
                // Customer distribution logic
                const customerKey = order.email || order.userId || order.phoneNumber || '';
                allCustomerOrderCounts.set(customerKey, (allCustomerOrderCounts.get(customerKey) || 0) + 1);
                if (createdAt >= currentMonthFilterDate) {
                    customerOrderCounts.set(customerKey, (customerOrderCounts.get(customerKey) || 0) + 1);
                } else if (createdAt >= previousMonthFilterDate && createdAt < currentMonthFilterDate) {
                    prevMonthCustomerOrderCounts.set(customerKey, (prevMonthCustomerOrderCounts.get(customerKey) || 0) + 1);
                }

                // ...existing widget stats...
                if (createdAt.toDateString() === effectiveDate.toDateString()) {
                    dailyRevenue += total;
                    dailyOrders += 1;
                }
                if (createdAt.getMonth() === effectiveDate.getMonth() && createdAt.getFullYear() === effectiveDate.getFullYear()) {
                    monthlyRevenue += total;
                    monthlyOrders += 1;
                }
                if (createdAt.toDateString() === yesterday.toDateString()) {
                    prevDailyRevenue += total;
                    prevDailyOrders += 1;
                }
                if (createdAt.getMonth() === lastMonth.getMonth() && createdAt.getFullYear() === lastMonth.getFullYear()) {
                    prevMonthlyRevenue += total;
                    prevMonthlyOrders += 1;
                }

                const hour = createdAt.toLocaleString('en-US', { hour: 'numeric', hour12: true });
                const day = createdAt.toLocaleDateString('en-US', { weekday: 'short' });
                const month = createdAt.toLocaleDateString('en-US', { month: 'short' });

                hourlyTrendMap.set(hour, (hourlyTrendMap.get(hour) || 0) + 1);
                hourlyRevenueTrendMap.set(hour, (hourlyRevenueTrendMap.get(hour) || 0) + Number(order.totalPaid ?? 0));
                if (createdAt >= hourlyFilterDate) {
                    hourlyMap.set(hour, (hourlyMap.get(hour) || 0) + 1);
                    hourlyRevenueMap.set(hour, (hourlyRevenueMap.get(hour) || 0) + Number(order.totalPaid ?? 0));
                }
                dailyTrendMap.set(day, (dailyTrendMap.get(day) || 0) + 1);
                dailyRevenueTrendMap.set(day, (dailyRevenueTrendMap.get(day) || 0) + Number(order.totalPaid ?? 0));
                if (createdAt >= dailyFilterDate) {
                    dailyMap.set(day, (dailyMap.get(day) || 0) + 1);
                    dailyRevenueMap.set(day, (dailyRevenueMap.get(day) || 0) + Number(order.totalPaid ?? 0));
                }
                monthlyTrendMap.set(month, (monthlyTrendMap.get(month) || 0) + 1);
                monthlyRevenueTrendMap.set(month, (monthlyRevenueTrendMap.get(month) || 0) + Number(order.totalPaid ?? 0));
                if (createdAt >= monthlyFilterDate) {
                    monthlyMap.set(month, (monthlyMap.get(month) || 0) + 1);
                    monthlyRevenueMap.set(month, (monthlyRevenueMap.get(month) || 0) + Number(order.totalPaid ?? 0));
                }

                if (createdAt >= currentMonthFilterDate) {
                    order.cartItems.forEach(item => {
                        const stats = productStats.get(item.name) || { orders: 0, prevMonthOrders: 0, revenue: 0 };
                        stats.orders += item.quantity;
                        stats.revenue += parseFloat(item.priceAfterTax) * item.quantity;
                        productStats.set(item.name, stats);
                    });
                }
                else if (createdAt >= previousMonthFilterDate && createdAt < currentMonthFilterDate) {
                    order.cartItems.forEach(item => {
                        const stats = productStats.get(item.name) || { orders: 0, prevMonthOrders: 0, revenue: 0 };
                        stats.prevMonthOrders += item.quantity;
                        productStats.set(item.name, stats);
                    });
                }
            });

            const percent = (curr: number, prev: number) => prev === 0 ? 100 : Math.round(((curr - prev) / prev) * 100);
            
            setOrderData({
                hourly: Array.from(hourlyMap.entries()).map(([name, orders]) => ({ name, orders, revenue: 0 })),
                daily: Array.from(dailyMap.entries()).map(([name, orders]) => ({ name, orders, revenue: 0 })),
                monthly: Array.from(monthlyMap.entries()).map(([name, orders]) => ({ name, orders, revenue: 0 }))
            });

            setOrderTrendData({
                hourly: Array.from(hourlyTrendMap.entries()).map(([name, orders]) => ({ name, orders, revenue: 0 })),
                daily: Array.from(dailyTrendMap.entries()).map(([name, orders]) => ({ name, orders, revenue: 0 })),
                monthly: Array.from(monthlyTrendMap.entries()).map(([name, orders]) => ({ name, orders, revenue: 0 }))
            });

            setRevenueData({
                hourly: Array.from(hourlyRevenueMap.entries()).map(([name, revenue]) => ({ name, revenue, orders: 0 })),
                daily: Array.from(dailyRevenueMap.entries()).map(([name, revenue]) => ({ name, revenue, orders: 0 })),
                monthly: Array.from(monthlyRevenueMap.entries()).map(([name, revenue]) => ({ name, revenue, orders: 0 }))
            });

            setRevenueTrendData({
                hourly: Array.from(hourlyRevenueTrendMap.entries()).map(([name, revenue]) => ({ name, revenue, orders: 0 })),
                daily: Array.from(dailyRevenueTrendMap.entries()).map(([name, revenue]) => ({ name, revenue, orders: 0 })),
                monthly: Array.from(monthlyRevenueTrendMap.entries()).map(([name, revenue]) => ({ name, revenue, orders: 0 }))
            });

            setStats({
                dailyRevenue,
                monthlyRevenue,
                dailyOrders,
                monthlyOrders,
                dailyRevenueChange: percent(dailyRevenue, prevDailyRevenue),
                monthlyRevenueChange: percent(monthlyRevenue, prevMonthlyRevenue),
                dailyOrdersChange: percent(dailyOrders, prevDailyOrders),
                monthlyOrdersChange: percent(monthlyOrders, prevMonthlyOrders)
            });

            const sortedProducts = Array.from(productStats.entries())
                .map(([name, stats]) => ({
                    name,
                    orders: stats.orders,
                    revenue: parseFloat(stats.revenue.toFixed(2)),
                    change: percent(stats.orders, stats.prevMonthOrders)
                }))
                .sort((a, b) => b.orders - a.orders).slice(0, 10);

            setBestSellingProducts(sortedProducts);
        };

        fetchOrders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectiveDate]);

    return (
        <Container className="main-container p-3">
            <h2 className="mb-4">Earnings Analytics</h2>
            {/* Top Widgets */}
            <Row className="mb-4">
                {[
                    {
                        title: "Daily Revenue",
                        value: Utils.FormatCurrency(stats.dailyRevenue, settings.currency),
                        change: stats.dailyRevenueChange,
                        comparison: "vs. yesterday",
                        icon: <DollarSign size={20} color="white" />,
                    },
                    {
                        title: "Monthly Revenue",
                        value: Utils.FormatCurrency(stats.monthlyRevenue, settings.currency),
                        change: stats.monthlyRevenueChange,
                        comparison: "vs. last month",
                        icon: <Calendar size={20} color="white" />,
                    },
                    {
                        title: "Daily Orders",
                        value: stats.dailyOrders,
                        change: stats.dailyOrdersChange,
                        comparison: "vs. yesterday",
                        icon: <ShoppingCart size={20} color="white" />,
                    },
                    {
                        title: "Monthly Orders",
                        value: stats.monthlyOrders,
                        change: stats.monthlyOrdersChange,
                        comparison: "vs. last month",
                        icon: <BarChart3 size={20} color="white" />,
                    }                  
                ].map((widget, idx) => (
                    <Col key={idx} xs={12} sm={6} md={3} className="mb-3">
                        <Card className="shadow border-0">
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                        <Card.Title className="mb-2">{widget.title}</Card.Title>
                                        <h4 className="mb-1">{widget.value}</h4>
                                        <div className={`${widget.change >= 0 ? 'text-success' : 'text-danger'} fw-medium`}>
                                            {`${widget.change > 0 ? '+' : ''}${widget.change}%`} <span className="text-muted">({widget.comparison})</span>
                                        </div>
                                    </div>
                                    <div
                                        style={{
                                            background: `linear-gradient(135deg, ${colors[0]} 0%, ${colors[1]} 100%)`,
                                            borderRadius: "0.5rem",
                                            padding: "0.5rem",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        {widget.icon}
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>

            <Row className="mb-4">
                {/* Revenue Trends */}
                <Col xs={12} md={8} className="mb-3">
                    <Card className="shadow border-0">
                        <Card.Body>
                            <Card.Title>
                                Total Revenue
                                <span className="text-muted" style={{ fontSize: '0.9rem', marginLeft: '10px' }}>
                                    {revenueFilter === 'hourly'
                                        ? effectiveDate.toLocaleDateString('en-ZA', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' })
                                        : revenueFilter === 'daily'
                                            ? `${new Date(effectiveDate.getTime() - 6 * 24 * 60 * 60 * 1000).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })} - ${effectiveDate.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })}`
                                            : `${new Date(effectiveDate.getFullYear() - 1, effectiveDate.getMonth(), 1).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })} - ${effectiveDate.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })}`}
                                </span>
                            </Card.Title>
                            <div className="text-muted mb-3">Track your earnings over time</div>
                            <ButtonGroup className="mb-3 me-2">
                                {['hourly', 'daily', 'monthly'].map((filter) => (
                                    <Button
                                        key={filter}
                                        variant={revenueFilter === filter ? 'primary' : 'outline-primary'}
                                        onClick={() => setRevenueFilter(filter as any)}
                                    >
                                        {filter.charAt(0).toUpperCase() + filter.slice(1)}
                                    </Button>
                                ))}
                            </ButtonGroup>
                            <ButtonGroup className="mb-3">
                                {['actual', 'trend'].map((type) => (
                                    <Button
                                        key={type}
                                        variant={revenueTrendToggle === type ? 'primary' : 'outline-primary'}
                                        onClick={() => setRevenueTrendToggle(type as 'actual' | 'trend')}
                                    >
                                        {type === 'trend' ? 'Trend' : 'Actual'}
                                    </Button>
                                ))}
                            </ButtonGroup>
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={revenueTrendToggle === 'trend' ? revenueTrendData[revenueFilter] : revenueData[revenueFilter]}>
                                    <XAxis dataKey="name" />
                                    <YAxis />
                                    <Tooltip
                                        formatter={(value: number, name: string) => [Utils.FormatCurrency(value, settings.currency), name.charAt(0).toUpperCase() + name.slice(1)]}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="revenue"
                                        stroke={colors[1]}
                                        strokeWidth={2}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </Card.Body>
                    </Card>
                </Col>

{/* Top Products */}
                <Col xs={12} md={4}>
                    <Card className="shadow border-0">
                        <Card.Body>
                            <Card.Title>Top 10 Products
                                <span className="text-muted" style={{ fontSize: '0.9rem', marginLeft: '10px' }}>
                                    {effectiveDate.toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' })}
                                </span>                                
                            </Card.Title>
                            <div className="text-muted mb-3">Best selling items this month</div>
                            {bestSellingProducts.map((product, i) => {
                                const maxOrders = Math.max(...bestSellingProducts.map(p => p.orders));
                                const barWidth = `${(product.orders / maxOrders) * 100}%`;
                                const changeColor = product.change >= 0 ? 'text-success' : 'text-danger';

                                return (
                                    <div key={i} className="mb-4">
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <span style={{ fontWeight: 'bold', fontSize: '1.05rem' }}>{product.name}</span>
                                            <span className="fw-medium">{Utils.FormatCurrency(product.revenue, settings.currency)}</span>
                                        </div>
                                        <div className="d-flex justify-content-between text-muted mb-1" style={{ fontSize: '0.9rem' }}>
                                            <span>{product.orders.toLocaleString()} orders</span>
                                            <span className={changeColor}>{product.change >= 0 ? '+' : ''}{product.change}%</span>
                                        </div>
                                        <div style={{ backgroundColor: '#E5E7EB', height: '8px', borderRadius: '4px' }}>
                                            <div
                                                style={{
                                                    width: barWidth,
                                                    backgroundColor: colors[1],
                                                    height: '100%',
                                                    borderRadius: '4px',
                                                }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                            <hr />
                            <div className="text-end fw-bold">
                                Total Products Sold: {bestSellingProducts.reduce((sum, p) => sum + p.orders, 0).toLocaleString()}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Total Orders & Best Selling Products */}
            <Row>
                {/* Total Orders */}
                <Col xs={12} md={8} className="mb-3">
                    <Card className="shadow border-0">
                        <Card.Body>
                            <Card.Title>
                                Total Orders
                                <span className="text-muted" style={{ fontSize: '0.9rem', marginLeft: '10px' }}>
                                    {orderFilter === 'hourly'
                                        ? effectiveDate.toLocaleDateString('en-ZA', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' })
                                        : orderFilter === 'daily'
                                            ? `${new Date(effectiveDate.getTime() - 6 * 24 * 60 * 60 * 1000).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })} - ${effectiveDate.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })}`
                                            : `${new Date(effectiveDate.getFullYear() - 1, effectiveDate.getMonth(), 1).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })} - ${effectiveDate.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })}`}
                                </span>
                            </Card.Title>
                            <div className="text-muted mb-3">Track order volume over time</div>
                            <ButtonGroup className="mb-3 me-2">
                                {['hourly', 'daily', 'monthly'].map((filter) => (
                                    <Button
                                        key={filter}
                                        variant={orderFilter === filter ? 'primary' : 'outline-primary'}
                                        onClick={() => setOrderFilter(filter as any)}
                                    >
                                        {filter.charAt(0).toUpperCase() + filter.slice(1)}
                                    </Button>
                                ))}
                            </ButtonGroup>
                            <ButtonGroup className="mb-3">
                                {['actual', 'trend'].map((type) => (
                                    <Button
                                        key={type}
                                        variant={orderTrendToggle === type ? 'primary' : 'outline-primary'}
                                        onClick={() => setOrderTrendToggle(type as 'actual' | 'trend')}
                                    >
                                        {type === 'trend' ? 'Trend' : 'Actual'}
                                    </Button>
                                ))}
                            </ButtonGroup>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={orderTrendToggle === 'trend' ? orderTrendData[orderFilter] : orderData[orderFilter]}>
                                    <defs>
                                        <linearGradient id="orderGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor={colors[1]} stopOpacity={0.8} />
                                            <stop offset="100%" stopColor={colors[1]} stopOpacity={0.3} />
                                        </linearGradient>
                                    </defs>
                                    <XAxis dataKey="name" />
                                    <YAxis />
                                    <Tooltip
                                        formatter={(value: number, name: string) => [value, name.charAt(0).toUpperCase() + name.slice(1)]}
                                    />
                                    <Bar dataKey="orders" fill="url(#orderGradient)" />
                                </BarChart>
                            </ResponsiveContainer>
                        </Card.Body>
                    </Card>
                </Col>                
            </Row>
        </Container>
    );
};

export default EarningsAnalytics;